import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wand2, Loader2, Sparkles, Coins, GitCompare, Mic, Zap } from "lucide-react";
import { LAYER_KINDS } from "@/components/SurgeLayerPanel";
import PremiumEngineToggle from "@/components/PremiumEngineToggle";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useGenerations from "@/hooks/useGenerations";
import GenerationCard from "@/components/GenerationCard";
import StyleControls from "@/components/StyleControls";
import { useEffect } from "react";
import ModelPreferenceBadge, { AiSourceTag, recordAiPreference } from "@/components/ModelPreference";

const GENRES = ["Electronic", "Cinematic", "Orchestral", "Synthwave", "Ambient", "Rock", "Hip-Hop", "Lo-Fi", "Chiptune", "Industrial"];
const MOODS = ["Dark", "Epic", "Cinematic", "Tense", "Uplifting", "Mysterious", "Aggressive", "Calm", "Heroic", "Melancholic"];
const DURATIONS = [{ v: 30, c: 1 }, { v: 60, c: 2 }, { v: 90, c: 3 }];

const PRESETS = [
  "Dark cinematic electronic music for a cyberpunk game boss fight",
  "Epic orchestral battle theme with heavy percussion and brass",
  "Calm ambient exploration loop for a sci-fi open world",
  "Retro synthwave chase sequence, driving arpeggios",
];

export default function Studio() {
  const { refreshUser, user } = useAuth();
  const { generations, refetch } = useGenerations();
  const [form, setForm] = useState({ prompt: "", genre: "Electronic", mood: "Dark", duration: 30, loop: true, stems: false, project_id: null, synth_layer: null, genre2: null, fusion_ratio: 50, ensemble: null, culture: null, mood2: null, mood_blend: 50, engine: "internal" });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState("openai");
  const [compare, setCompare] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [varLoading, setVarLoading] = useState(null);
  const [projects, setProjects] = useState([]);
  const [promptSource, setPromptSource] = useState(null);

  const enhancePrompt = async () => {
    if (!form.prompt.trim()) { toast.error("Type a short idea first, then enhance it."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/enrich-prompt", { idea: form.prompt.trim(), provider: aiProvider });
      applyEnriched(data, aiProvider);
      toast.success(`Prompt enhanced with ${aiProvider === "claude" ? "Claude" : "ChatGPT"} ✨`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI enhance failed.");
    } finally { setAiLoading(false); }
  };

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    const q = new URLSearchParams(window.location.search);
    if (q.get("welcome") === "tiktok") { setTimeout(() => toast.success("Welcome! Your TikTok account is linked — 20 free credits are ready. Describe your first sound below.", { duration: 10000 }), 600); window.history.replaceState({}, "", window.location.pathname); }
    if (q.get("prompt")) {
      const genre = GENRES.find((g) => g.toLowerCase() === (q.get("genre") || "").toLowerCase());
      const mood = MOODS.find((m) => m.toLowerCase() === (q.get("mood") || "").toLowerCase());
      setForm((f) => ({ ...f, prompt: q.get("prompt"), ...(genre ? { genre } : {}), ...(mood ? { mood } : {}) }));
      toast.success("Prompt loaded from the showcase — tweak it and generate your own version.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const applyEnriched = (data, provider) => {
    setForm((f) => ({
      ...f,
      prompt: data.prompt || f.prompt,
      genre: GENRES.includes(data.genre) ? data.genre : (GENRES.find((g) => (data.genre || "").toLowerCase().includes(g.toLowerCase())) || f.genre),
      mood: MOODS.find((m) => (data.mood || "").toLowerCase().includes(m.toLowerCase())) || f.mood,
    }));
    setPromptSource(provider);
    recordAiPreference(provider, "prompt");
  };

  const compareModels = async () => {
    if (!form.prompt.trim()) { toast.error("Type a short idea first, then compare."); return; }
    setCompareLoading(true);
    setCompare(null);
    try {
      const [openai, claude] = await Promise.all([
        api.post("/ai/enrich-prompt", { idea: form.prompt.trim(), provider: "openai" }),
        api.post("/ai/enrich-prompt", { idea: form.prompt.trim(), provider: "claude" }),
      ]);
      setCompare({ openai: openai.data, claude: claude.data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI comparison failed.");
    } finally { setCompareLoading(false); }
  };

  const cost = (form.engine === "premium" ? 0 : Math.max(1, Math.ceil(form.duration / 30)) + (form.stems ? 2 : 0)) + (form.synth_layer ? 1 : 0);
  const recent = generations.slice(0, 6);

  const generate = async () => {
    if (!form.prompt.trim()) {
      toast.error("Describe the music you want to generate.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/generations", form);
      toast.success("Generation started — rendering your track.");
      await refetch();
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onVariation = async (id) => {
    setVarLoading(id);
    try {
      await api.post(`/generations/${id}/variation`);
      toast.success("Variation started.");
      await refetch();
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setVarLoading(null);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/generations/${id}`);
      await refetch();
    } catch {
      toast.error("Could not delete generation.");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl overflow-x-hidden">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
            <Wand2 className="text-primary" /> Studio
          </h1>
          <p className="text-muted-foreground mt-1">Generate a soundtrack from a single prompt.</p>
          <ModelPreferenceBadge className="mt-2" />
        </div>
        <Link
          to={form.project_id ? `/dashboard/voice?project=${form.project_id}` : "/dashboard/voice"}
          className="inline-flex items-center gap-2 rounded-md border border-secondary/40 text-secondary hover:bg-secondary/10 h-10 px-4 text-sm font-medium transition-colors"
          data-testid="quick-voice-studio"
        >
          <Mic size={15} /> Générer une voix / SFX
        </Link>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Generator */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 min-w-0 rounded-2xl border border-border bg-card p-6 space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Describe your music</Label>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setAiProvider((p) => (p === "openai" ? "claude" : "openai"))} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30" data-testid="ai-provider-toggle" title="Switch AI model">
                  {aiProvider === "openai" ? "ChatGPT" : "Claude"}
                </button>
                <Button onClick={enhancePrompt} disabled={aiLoading} variant="ghost" size="sm" className="h-7 text-primary hover:text-primary hover:bg-primary/10 gap-1.5" data-testid="ai-enhance-prompt">
                  {aiLoading ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Enhance with AI
                </Button>
                <Button onClick={compareModels} disabled={compareLoading} variant="ghost" size="sm" className="h-7 text-secondary hover:text-secondary hover:bg-secondary/10 gap-1.5" data-testid="ai-compare-models">
                  {compareLoading ? <Loader2 className="animate-spin" size={13} /> : <GitCompare size={13} />} Comparer
                </Button>
              </div>
            </div>
            <Textarea
              value={form.prompt}
              onChange={(e) => { setForm({ ...form, prompt: e.target.value }); setPromptSource(null); }}
              placeholder="e.g. Dark cinematic electronic music for a cyberpunk game…"
              className="min-h-24 bg-background border-border focus-visible:ring-primary resize-none"
              data-testid="prompt-input"
            />
            {promptSource && (
              <div className="flex items-center gap-2">
                <AiSourceTag provider={promptSource} prefix="Prompt généré par" testId="prompt-ai-label" />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  onClick={() => setForm({ ...form, prompt: p })}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors flex items-center gap-1"
                  data-testid="preset-prompt"
                >
                  <Sparkles size={10} /> {p.slice(0, 34)}…
                </button>
              ))}
            </div>

            {compare && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="compare-results">
                <CompareCard label="ChatGPT" data={compare.openai} onUse={() => { applyEnriched(compare.openai, "openai"); toast.success("Suggestion ChatGPT appliquée ✨"); }} testId="compare-openai" />
                <CompareCard label="Claude" data={compare.claude} onUse={() => { applyEnriched(compare.claude, "claude"); toast.success("Suggestion Claude appliquée ✨"); }} testId="compare-claude" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Genre">
              <Select value={form.genre} onValueChange={(v) => setForm({ ...form, genre: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="genre-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border max-h-60">
                  {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Mood">
              <Select value={form.mood} onValueChange={(v) => setForm({ ...form, mood: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="mood-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border max-h-60">
                  {MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Duration">
              <Select value={String(form.duration)} onValueChange={(v) => setForm({ ...form, duration: Number(v) })}>
                <SelectTrigger className="bg-background border-border" data-testid="duration-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">
                  {DURATIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.v}s · {d.c} cr</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <StyleControls value={form} onChange={(v) => setForm((f) => ({ ...f, ...v }))} genres={GENRES} genre={form.genre} moods={MOODS} mood={form.mood} testPrefix="studio-style" />

          <div className="grid grid-cols-2 gap-3">
            <ToggleRow label="Loopable" desc="Seamless game loop" checked={form.loop} onChange={(v) => setForm({ ...form, loop: v })} testId="loop-switch" />
            <ToggleRow label="Stems" desc="Drums · Bass · Melody (+2 cr)" checked={form.stems} onChange={(v) => setForm({ ...form, stems: v })} testId="stems-switch" />
          </div>
          <PremiumEngineToggle enabled={form.engine === "premium"} onChange={(v) => setForm({ ...form, engine: v ? "premium" : "internal" })} duration={form.duration} stems={form.stems} synthLayer={!!form.synth_layer} refreshKey={form.engine} isAdmin={user?.role === "admin"} />
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.05] px-4 py-3" data-testid="surge-layer-row">
            <div>
              <div className="text-sm font-medium flex items-center gap-1.5"><Zap size={14} className="text-amber-400" /> Couche Surge XT</div>
              <div className="text-xs text-muted-foreground">Ajoute automatiquement un synthé Surge dans la tonalité et au tempo de la piste (+1 cr)</div>
            </div>
            <div className="flex items-center gap-2">
              {form.synth_layer && (
                <Select value={form.synth_layer.kind} onValueChange={(v) => setForm({ ...form, synth_layer: { ...form.synth_layer, kind: v } })}>
                  <SelectTrigger className="h-8 w-28 text-xs bg-background border-border" data-testid="surge-layer-kind"><SelectValue /></SelectTrigger>
                  <SelectContent className="glass border-border">{LAYER_KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Switch checked={!!form.synth_layer} onCheckedChange={(v) => setForm({ ...form, synth_layer: v ? { kind: "arp", mix: 0.35, patch_id: "" } : null })} data-testid="surge-layer-switch" />
            </div>
          </div>

          <Field label="Assign to project (optional)">
            <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? null : v })}>
              <SelectTrigger className="bg-background border-border" data-testid="project-select"><SelectValue placeholder="No project" /></SelectTrigger>
              <SelectContent className="glass border-border max-h-60">
                <SelectItem value="none">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Coins size={15} className="text-primary" /> Cost: <span className="font-mono font-bold text-foreground">{cost}</span> credits{form.engine === "premium" && <span className="text-fuchsia-300" data-testid="studio-premium-cost"> + {Math.max(1, Math.ceil(form.duration / 30)) + (form.stems ? 1 : 0)} premium{form.synth_layer ? " (1 cr interne = couche Surge)" : ""}</span>}
              <span className="text-xs">· you have {user?.credits ?? 0}</span>
            </div>
            <Button onClick={generate} disabled={loading} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary px-6" data-testid="generate-button">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={16} className="mr-1.5" />}
              GENERATE
            </Button>
          </div>
        </motion.div>

        {/* Info panel */}
        <div className="lg:col-span-2 min-w-0 rounded-2xl border border-border bg-card p-6 relative overflow-hidden">
          <img src="https://images.unsplash.com/photo-1632582204758-5ac65783517a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
          <div className="relative">
            <h3 className="font-display text-xl font-bold mb-3">Every track ships with</h3>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li className="flex gap-2"><span className="text-primary">▸</span> MP3 + lossless WAV downloads</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Loop-optimized version for games</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Individual stems on demand</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Perpetual commercial license</li>
              <li className="flex gap-2"><span className="text-primary">▸</span> Instant variations</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recent generations */}
      <section className="mt-10">
        <h2 className="font-display text-2xl font-extrabold mb-4">Your Generations</h2>
        {recent.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="empty-generations">
            No tracks yet. Generate your first soundtrack above.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {recent.map((g) => (
              <GenerationCard key={g.id} gen={g} onVariation={onVariation} onDelete={onDelete} varLoading={varLoading === g.id} onSaved={refetch} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const ToggleRow = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-center justify-between rounded-xl border border-border bg-background px-4 py-3">
    <div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);


const CompareCard = ({ label, data, onUse, testId }) => (
  <div className="rounded-xl border border-secondary/25 bg-secondary/[0.04] p-3 flex flex-col" data-testid={testId}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[10px] font-mono text-secondary">{label}</span>
      <span className="text-[10px] font-mono text-muted-foreground">{data.genre} · {data.mood}</span>
    </div>
    <p className="text-xs text-foreground/90 leading-relaxed flex-1">{data.prompt}</p>
    <Button onClick={onUse} size="sm" className="mt-2.5 h-8 bg-secondary text-white hover:bg-secondary/90 self-start" data-testid={`${testId}-use`}>
      Utiliser cette version
    </Button>
  </div>
);
